<template>
  <div v-if="subscription && !activeproduct">
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" class="p-2">
        <b class="ml-2" style="color: red">Inactive Subscription Warning</b>
      </b-card-header>
      <b-card-body>
        <div>
          You currently have a subscription that has failed to renew.<br />
          This could mean a payment has failed, which is most commonly caused by
          recieving a new bank card.<br />
          Please remove your old subscription via the
          <router-link to="/adjustplan">Adjust Current Plan</router-link> page
          and add a new one to regain access.
        </div>
      </b-card-body>
    </b-card>
  </div>
  <div v-else-if="!subscription" class="accordion" role="tablist">
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" class="p-2">
        <b class="ml-2">Choose your plan</b>
      </b-card-header>
      <b-card-body>
        <div>
          <CContainer>
            <current-plan-details :datediff="datediff(activeuntil) > 0 ? datediff(activeuntil) : undefined"></current-plan-details>
            <CRow class="mb-1"> Select your preferred product: </CRow>
            <CRow class="pb-2">
              <b-button-group
                size="sm"
                aria-label="Period selection"
                v-model="selectedPeriod"
                class="central"
              >
                <b-button
                  v-for="(btn, idx) in buttons"
                  :key="idx"
                  type="radio"
                  :class="{
                    'cut-btn': true,
                    selected: btn.period === selectedPeriod,
                  }"
                  :title="btn.period === 'FLEXI' && datediff(activeuntil) > 1 ? 'As you have a pre-paid plan, you cannot currently convert to a FlexiPlan.' : undefined"
                  :disabled="btn.period === 'FLEXI' && datediff(activeuntil) > 1"
                  @click="selectPeriod(btn.period)"
                  >{{ btn.label }}
                </b-button>
              </b-button-group>
            </CRow>
            <CRow v-if="selectedPeriod == 'MONTH'"> Discount code: </CRow>
            <CRow class="mb-2" v-show="selectedPeriod == 'MONTH'">
              <special-offer
                ref="specialOffer"
                class="central"
                @applySpecial="setSpecialAndPrices"
              />
            </CRow>
            <CRow v-if="selectedPeriod == 'MONTH'">
              <b-card-group style="margin: auto" deck>
                <subscription-product-card
                  v-for="product in products"
                  :key="product.productid"
                  :ref="'svc' + product.productid"
                  :service="product"
                  :datediff="datediff(activeuntil)"
                  :isActive="
                    (activeproduct &&
                      activeproduct.productid === product.productid) ||
                    (subscription &&
                      subscription.product.productid === product.productid)
                  "
                  :price="
                    prices.find((p) => p.productid === product.productid)
                      .txnPrice
                  "
                  :monthlyPrice="
                    prices.find((p) => p.productid === product.productid)
                      .monthlyPrice
                  "
                  :until="nextPaymentDate(product.productid)"
                  @productSelect="clicked(product.productid)"
                >
                  <template #action>
                    <br />
                    <b-button
                      class="mx-4 mb-4"
                      :disabled="
                        selectedProduct.productid === product.productid
                      "
                      href="#"
                      @click="clicked(product.productid)"
                      >{{ productButtonContent(product.productid) }}</b-button
                    >
                  </template>
                </subscription-product-card>
              </b-card-group>
            </CRow>
            <br />
            <CRow v-if="selectedPeriod == 'MONTH'">
              <div class="centre-content" style="color: red;">
                A subscription will continue to take payment
                <span v-if="planPrice != null" style="color: red;">
                  of £{{ planPrice.toFixed(2) }}&nbsp;</span
                >every month until you unsubscribe.
              </div>
            </CRow>
            <CRow v-if="selectedPeriod == 'FLEXI'">
              <CCol class="px-1">
                <CRow>
                  <p class="m-auto pb-4 px-2">
                    FlexiPlan
                    <strong>credits will be consumed on a daily basis</strong>
                    until all credits have been used
                    <strong>(including today)</strong>.
                    <br />
                    <strong>This cannot be paused after purchase</strong>, so
                    please purchase only the credits you know you require.
                    <br />
                    The base tier will consume 1 credit per day, then 2 for the
                    middle tier and 3 for the highest tier.
                  </p>
                </CRow>
                <CRow :style="'display: flex; justify-content: center; align-items: center; margin-bottom: 1.5rem;'">
                  <div :style="'display: flex; justify-content: center; align-items: center;'">
                    <b-card-group deck>
                      <flexi-product-card v-for="product in flexiProducts" :key="'flexi-' + product.productid" :product="product" :isActive="(activeproduct && activeproduct.productid === product.productid) || (subscription && subscription.product.productid === product.productid)" :selectedProduct="selectedProduct" :minimumCredits="filterCredits(product.productid)" @productSelect="clicked(product.productid)">
                        <template #action>
                          <b-button
                            class="mx-2 mb-2 mt-3"
                            :disabled="
                              selectedProduct.productid === product.productid
                            "
                            href="#"
                            @click="clicked(product.productid)"
                            >{{ productButtonContent(product.productid) }}</b-button
                          >
                        </template>
                      </flexi-product-card>
                    </b-card-group>
                  </div>
                </CRow>
              </CCol>
            </CRow>
            <br />
            <CRow>
              <div class="centre-content">
                <img class="visa-icon" src="@/assets/images/visa-logo.png" />
                <img
                  class="maestro-icon"
                  src="@/assets/images/maestro-logo.png"
                />
                <img class="jcb-icon" src="@/assets/images/jcb-logo.svg" />
                <img
                  class="mastercard-icon"
                  src="@/assets/images/mastercard-logo.png"
                />
              </div>
            </CRow>
          </CContainer>
        </div>
      </b-card-body>
    </b-card>

    <b-card v-if="selectedPeriod === 'MONTH'" no-body class="mb-1">
      <b-card-header header-tag="header" class="p-1" role="tab">
        <b-button
          type="submit"
          block
          v-b-toggle.accordion-3
          variant="info"
          @click="showPurchaseModal()"
          :disabled="selectedProduct === undefined"
          >Confirm</b-button
        >
      </b-card-header>
    </b-card>
    <b-card v-else no-body class="mb-1">
      <b-card-header header-tag="header" class="p-1" role="tab">
        <b-button
          type="submit"
          block
          v-b-toggle.accordion-3
          variant="info"
          @click="
            () => {
              flexdetails.productid = 1;
              flexiModal = true;
            }
          "
          :disabled="quantity === undefined || creditPrice == undefined"
          >Confirm and Pay</b-button
        >
      </b-card-header>
    </b-card>

    <b-modal
      size="md"
      v-model="purchaseModal"
      ok-only
      hide-backdrop
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      centered
      content-class="modal-radius"
      header-class="modal-header"
    >
      <template #modal-header
        ><b>Choose Subscription Payment Details</b></template
      >
      <template #default>
        <b-card-sub-title class="mx-3 mt-1" :checked="purchaseModal"
          >You wish to subscribe to this plan for £{{
            Number(subscribeWPDetails.amount).toFixed(2)
          }}</b-card-sub-title
        >
        <br />
        <span class="ml-3"><strong>Payment Breakdown</strong></span>
        <br />
        <div class="ml-3" style="white-space: pre-line">
          {{
            prices.find((p) => p.productid === selectedProduct.productid).note
          }}
        </div>
        <div class="ml-3" style="white-space: pre-line; color: red; font-weight: bold;">
          This subscription will continue to take payment
          <span v-if="planPrice != null">
            of £{{ planPrice.toFixed(2) }}&nbsp;</span
          >every month until you unsubscribe.
        </div>
      </template>
      <template #modal-footer>
        <CButton
          class="btn-outline-danger back-btn"
          @click="purchaseModal = false"
          >Cancel</CButton
        >
        <form :action="worldpay.baseurl + 'purchase'" method="POST" ref="subscribeForm" @submit.prevent="handleFormSubmit('subscribeForm')">
          <!-- Specifies the URL for our test environment -->
          <input type="hidden" name="testMode" :value="worldpay.testMode" />
          <!-- 100 instructs our test system - could use node environment variable to diff between production and development -->
          <input type="hidden" name="instId" :value="worldpay.instRef" />
          <!-- A mandatory parameter -->
          <input type="hidden" name="cartId" value="SoldierNET Broadband" />
          <!-- A mandatory parameter - reference for the item purchased -->
          <!-- SNet fields - Common Mandatory -->

          <input type="hidden" name="M_username" :value="account.username" />
          <input
            type="hidden"
            name="M_productid"
            :value="subscribeWPDetails.productid"
          />
          <!-- PERIOD BELOW MAY NOT BE REQUIRED -->
          <input type="hidden" name="M_period" value="MONTH" />
          <!-- Additional action type - SUBSCRIBEPAY, SUBSCRIBEONLY or FLEXICREDITS -->
          <input
            type="hidden"
            name="M_ACTION"
            :value="
              subscribeWPDetails.amount > 0 || (subscribeWPDetails.amount == 0 && special == null) ? 'SUBSCRIBEPAY' : 'SUBSCRIBEONLY'
            "
          />
          <!-- NEED SITE WIRED IN -->
          <input type="hidden" name="M_siteid" :value="site.siteid" />
          <!-- NEED CLIENT PORTAL ADDRESS -->
          <input
            v-if="onnet && status != null && status.session != null"
            type="hidden"
            name="M_cpAddress"
            :value="status.session.lease.ip"
          />
          <!-- Initial payment amount -->
          <input
            type="hidden"
            name="amount"
            :value="subscribeWPDetails.amount"
          />
          <!-- A mandatory parameter -->
          <input type="hidden" name="currency" value="GBP" />
          <!-- A mandatory parameter. ISO currency code -->
          <input type="hidden" name="hideCurrency" />
          <!-- SNet fields - If initial payment -->
          <input
            type="hidden"
            name="M_until"
            :value="subscribeWPDetails.nextPaymentDate"
          />
          <!-- futurepay -->
          <input type="hidden" name="futurePayType" value="limited" />
          <!-- limited or regular (may be unlimited) -->
          <input type="hidden" name="option" value="0" />
          <!-- redirect URL -->
          <input type="hidden" name="M_redirect" :value="host" />
          <!-- MD5 signed and timeout -->
          <input type="hidden" name="signature" :value="signature" />
          <input type='hidden' name='signatureFields' value='instId:amount:currency' />
          <input type="hidden" name=authValidTo :value="authValidUntil(15)">
          <!-- SNet fields - If futurepay -->
          <input
            v-if="special != null"
            type="hidden"
            name="M_specialid"
            :value="special.id"
          />
          <CButton
            v-if="
              special != null &&
              subscribeWPDetails.amount <= 0 &&
              selectedProduct.productid === 1
            "
            type="button"
            class="btn-info"
            :disabled="
              subscribeWPDetails.amount == null &&
              host == null &&
              special === null
            "
            @click="subscriptionWithNoPayment()"
            >Confirm and Pay</CButton
          >
          <CButton
            v-else
            type="submit"
            class="btn-info"
            :disabled="subscribeWPDetails.amount == null && host == null"
            
            >Confirm and Pay</CButton
          >
          <!-- @click="
              () => {
                startpaywallClick();
                unsubscribeForNewSubscription();
              }
            " -->
        </form>
      </template>
    </b-modal>

    <b-modal
      size="md"
      v-model="flexiModal"
      ok-only
      hide-backdrop
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      centered
      content-class="modal-radius"
      header-class="modal-header"
    >
      <template #modal-header><b>Choose FlexiPlan Credit Package</b></template>
      <template #default>
        <b-card
          no-body
          class="mb-1"
          style="margin: auto; max-width: 20rem"
        >
          <b-card-body>
            <CRow v-if="activeproduct && filterCredits(selectedProduct.productid) > 1" :style="'display: flex; justify-content: center; align-items: center; min-width: fit-content; margin-bottom: 1rem;'">
              <strong>Minimum purchase of {{ filterCredits(selectedProduct.productid) }} credits</strong>
            </CRow>
            <CRow class="mx-auto">
              <b-input-group>
                <span
                  v-for="credit in flexiPrices"
                  :key="credit.credits + '-' + credit.price"
                >
                    <b-radio
                    v-model="creditPrice"
                    class="mx-2"
                    :value="credit.price"
                    style="width: 16rem"
                    @change="applyCredits(credit)"
                    >Get {{ credit.credits }} FlexiPlan credits for £{{
                      Number(credit.price).toFixed(2)
                    }}<br />(£{{
                      Number(credit.price / credit.credits).toFixed(2)
                    }}
                    per credit)</b-radio
                  >
                  <br />
                </span>
              </b-input-group>
            </CRow>
            <CRow v-if="selectedPeriod == 'FLEXI' && planPrice != null">
              <span class="mt-1 flexi-input"> How many packs? </span>
              <span class="mt-2 flexi-input">
                <input
                  type="number"
                  v-model="quantity"
                  min="1"
                  max="10"
                  @input="validateInput"
                  @change="
                    applyCredits(
                      flexiPrices.find((c) => c.price === creditPrice)
                    )
                  "
                />
              </span>
              <br /><br />
              <span
                class="mt-2 flexi-input"
                style="
                  margin-left: 1rem;
                  margin-right: 1rem;
                  max-width: 16rem;
                "
              >
                Buying {{ quantity || 0 }} of the selected pack brings
                your total cost to £{{
                  (quantity * creditPrice).toFixed(2)
                }}.
              </span>
            </CRow>
          </b-card-body>
        </b-card>
      </template>
      <template #modal-footer>
        <CButton class="btn-outline-danger back-btn" @click="flexiModal = false">Cancel</CButton>
        <CButton class="btn-info" @click="() => {flexiModal = false; getMD5(quantity * creditPrice); confirmFlexiModal = true;}">Continue</CButton>
      </template>
    </b-modal>
    <b-modal
      size="md"
      v-model="confirmFlexiModal"
      ok-only
      hide-backdrop
      no-close-on-esc
      no-close-on-backdrop
      centered
      content-class="modal-radius"
      header-class="modal-header"
    >
      <template #modal-header><b>Choose FlexiPlan Credit Package</b></template>
      <template #default>
        <h5 class="mx-3">Please confirm the below details and pay.</h5>
        <h4 class="mx-3">
          <strong>Total</strong>: £{{ (quantity * creditPrice).toFixed(2) }} ({{ quantity }} pack{{ quantity === 1 ? '' : 's' }} at £{{ creditPrice.toFixed(2) }} each)
        </h4>
        <span class="mx-3"
          >Credits are utilized on a daily basis, from 00:00 AM to 23:59
          PM.</span
        >
      </template>
      <template #modal-footer>
        <CButton class="btn-outline-danger back-btn" @click="() => {confirmFlexiModal = false; flexiModal = true;}">Update details</CButton>
        <form :action="worldpay.baseurl + 'purchase'" method="POST" ref="flexForm" @submit.prevent="handleFormSubmit('flexForm')">
          <!-- Specifies the URL for our test environment -->
          <input type="hidden" name="testMode" :value="worldpay.testMode" />
          <!-- 100 instructs our test system - could use node environment variable to diff between production and development -->
          <input type="hidden" name="instId" :value="worldpay.instRef" />
          <!-- A mandatory parameter -->
          <input type="hidden" name="cartId" value="SoldierNET Broadband" />
          <!-- A mandatory parameter - reference for the item purchased -->
          <!-- SNet fields - Common Mandatory -->

          <input type="hidden" name="M_username" :value="account.username" />
          <input
            type="hidden"
            name="M_productid"
            :value="selectedProduct.productid"
          />
          <!-- Additional action type - SUBSCRIBEPAY, SUBSCRIBEONLY or FLEXICREDITS -->
          <input type="hidden" name="M_ACTION" value="FLEXICREDITS" />
          <!-- NEED SITE WIRED IN -->
          <input type="hidden" name="M_siteid" :value="site.siteid" />

          <!-- NEED CLIENT PORTAL ADDRESS -->
          <input
            v-if="onnet && status != null && status.session != null"
            type="hidden"
            name="M_cpAddress"
            :value="status.session.lease.ip"
          />

          <!-- Initial payment amount -->
          <input type="hidden" name="M_credits" :value="flexdetails.credits" />
          <!-- Initial payment amount -->
          <input type="hidden" name="amount" :value="quantity * creditPrice" />
          <!-- A mandatory parameter -->
          <input type="hidden" name="currency" value="GBP" />
          <!-- A mandatory parameter. ISO currency code -->
          <input type="hidden" name="hideCurrency" />
          <!-- limited or regular (may be unlimited) -->
          <input type="hidden" name="option" value="0" />
          <!-- redirect -->
          <input type="hidden" name="M_redirect" :value="host" />
          <!-- MD5 signed -->
          <input type="hidden" name="signature" :value="signature" />
          <input type='hidden' name='signatureFields' value='instId:amount:currency' />
          <input type="hidden" name=authValidTo :value="authValidUntil(15)">
          <CButton
            type="submit"
            class="btn-info"
            :disabled="flexdetails.credits == null || (flexdetails.credits < (filterCredits(selectedProduct.productid) - (account.flexiplan ? account.flexiplan.credits : 0)))"
            
            >Confirm and Pay for FlexiPlan</CButton
          >
          <!-- @click="
              () => {
                startpaywallClick();
              }
            " -->
        </form>
      </template>
    </b-modal>
  </div>

  <div v-else>
    To access this page you must not have a subscription. To amend your current
    plan go to <router-link to="/adjustplan">Adjust Current Plan</router-link>.
  </div>
</template>

<script>
import SpecialOffer from "../../components/specials/SpecialOffer.vue";
import SubscriptionProductCard from "../../components/product/SubscriptionProductCard.vue";
import CurrentPlanDetails from "../../components/general/CurrentPlanDetails.vue";
import { mapGetters, mapActions, mapState } from "vuex";
import { startpaywall, checkMyStatus } from "src/cpd";
import FlexiProductCard from '../../components/product/FlexiProductCard.vue';
export default {
  name: "CreatePlan",
  components: {
    SubscriptionProductCard,
    SpecialOffer,
    CurrentPlanDetails,
    FlexiProductCard
  },
  data: () => ({
    buttons: [
      {
        label: "Subscribe",
        period: "MONTH",
      },
      {
        label: "Pay as You Go",
        period: "FLEXI",
      },
    ],
    subscribeWPDetails: {
      amount: 0,
      nextPaymentDate: undefined,
      productid: undefined,
    },
    flexdetails: {
      credits: 1,
      productid: 1,
    },
    signature: "",
    purchaseModal: false,
    flexiModal: false,
    confirmFlexiModal: false,
    quantity: 1,
    creditPrice: 3.5,
    minimumCredits: null
  }),
  computed: {
    ...mapGetters({
      products: "pricing/getProducts",
      prices: "pricing/getPrices",
      flexiPrices: "pricing/getFlexiPrices",
      selectedProduct: "pricing/getChosenProduct",
      selectedPeriod: "pricing/getChosenPeriod",
      special: "pricing/getSpecial",
      subscription: "plan/getSubscription",
      activeproduct: "plan/getActiveProduct",
      activeuntil: "plan/getActiveUntil",
      minimumPrice: "pricing/getMinimumPrice",
      site: "account/site",
      worldpay: "pricing/getWorldpay",
      account: "account/account"
    }),

    ...mapState("captive", {
      onnet: (state) => state.onnet,
      status: (state) => state.status,
    }),
    ...mapActions('pricing', ['setChosenAdjustedProduct']),

    mac() {
      return this.status?.session?.lease?.ethernet || null;
    },
    ipAddress() {
      return this.status?.session?.lease?.ip || null;
    },

    planPrice: function () {
      console.log(JSON.stringify(this.prices))
      return this.prices.find(
        (p) => p.productid === this.selectedProduct.productid
      ).monthlyPrice;
    },
    flexiProducts() {
      return this.products.filter(
        (p) => p.serviceType === "BASE" && p.credits != null
      );
    },
    host() {
      return window.location.host;
    }
  },
  watch: {
    selectedPeriod: {
        immediate: true,
        handler(newVal) {
            this.$log.onDebug('new val for selected period is ' + JSON.stringify(newVal))
            if (newVal == "FLEXI") {
              this.$portalapi.get('/minimumcredits').then((response) => {
                this.minimumCredits = response.data;
              });
            }
        },
    }
  },
  methods: {
    async handleFormSubmit(ref) {
      console.log('handle form submit method beginning')
      try {
        console.log('about to await start paywall')
        let paywallResponse;
        if ( this.$store.getters['captive/isOnNet'] ) {
          paywallResponse = await startpaywall(this.ipAddress, this.mac);
        } else {
          paywallResponse = true;
        }
        console.log(JSON.stringify(paywallResponse))
        console.log('this is to be sure')
        if (paywallResponse) {
          // If response is successful, submit the form
          this.$refs[ref].submit();
        } else {
          // Handle errors or unsuccessful conditions
          console.error("Failed to initialize paywall");
        }
      } catch (error) {
        console.error("Error in submitting paywall request:", error);
      }
    },
    async setSpecialAndPrices(payload) {
      console.log('Special payload is ' + JSON.stringify(payload))
      await this.$store.dispatch("pricing/setSpecial", payload);
      this.$store.dispatch("pricing/initialisePrices", false);
      this.setSubscribeWPDetails();
    },
    async subscriptionWithNoPayment() {
      this.unsubscribeForNewSubscription();

      await this.$portalapi
        .get("snetsubscribe", {
          params: {
            specialid: this.special?.id,
            until: this.subscribeWPDetails.nextPaymentDate,
          },
        })
        .then((response) => {
          console.log('update in sub');
          this.$router.push("/account/info");
        })
        .catch((error) => {
          alert("ERROR\nSubscription creation failed.");
          this.$log.onDebug(error.data);
        });
      this.$log.onDebug('Unsubscribe with no payment method complete.')
    },
    // async startpaywallClick() {
    //   console.log(this.ipAddress)
    //   console.log(this.mac)
    //   console.log('startpaywall function')
    //   await startpaywall(this.ipAddress, this.mac);
    // },
    getMD5(amount) {
      this.$portalapi.get("worldpay/prepare", { params: { amount: amount, currency: "GBP" } }).then((response) => {
        this.signature = response.data;
        this.$log.onDebug("Prepared data for worldpay transaction has been recieved - " + JSON.stringify(response.data));
      },
      (error) => {
        this.$log.onError("Failed to prepare for worldpay transaction due to error - " + JSON.stringify(error))
      })
    },
    filterCredits(productid) {
      let filtered = this.minimumCredits.filter(c => c.productid == productid);
      return filtered.length > 0 ? filtered[0].credits : null;
    },
    authValidUntil(timeoutMins) {
      // Get the current time
      var now = new Date();

      // Add specified minutes (15) to the current time
      now.setMinutes(now.getMinutes() + timeoutMins);

      // Return the time as a Unix timestamp
      return Math.floor(now.getTime());
    },
    unsubscribeForNewSubscription() {
      if (this.subscription && !this.activeproduct) {
        this.$portalapi.get("unsubscribe").then(
          (response) => {
            this.$log.onDebug("removed existing subscription");
          },
          (error) => {
            this.$log.onError(
              "failed to unsubscribe account due to error - " +
                JSON.stringify(error)
            );
          }
        );
      }
    },
    applyCredits(pack) {
      if (this.quantity < 1) this.quantity = 1;
      this.flexdetails.credits = pack.credits * this.quantity;
    },
    validateInput() {
      if (this.quantity < 0) {
        this.quantity = 1;
      } else if (this.quantity > 10) {
        this.quantity = 10;
      }
    },
    setSubscribeWPDetails() {
      this.subscribeWPDetails.amount = this.prices.find(
        (p) => p.productid === this.selectedProduct.productid
      ).txnPrice;
      this.subscribeWPDetails.nextPaymentDate =
        this.$options.filters.dateFormat(
          this.nextPaymentDate(this.selectedProduct.productid),
          "YYYY-MM-DD HH:mm:ss"
        );
      this.subscribeWPDetails.productid = this.selectedProduct.productid;
    },
    showAlert(content) {
      alert(content);
    },
    showPurchaseModal() {
      this.setSubscribeWPDetails();
      this.getMD5(this.subscribeWPDetails.amount);
      this.purchaseModal = true;
    },

    nextPaymentDate(productid) {
      console.log(JSON.stringify(this.prices))
      return new Date(
        this.prices.find((p) => p.productid === productid).toDate
      );
    },
    clicked(productid) {
      this.setChosenProduct(productid);
    },
    ...mapActions({
      setChosenProduct: "pricing/setChosenProduct",
      setChosenPeriod: "pricing/setChosenPeriod",
      getSite: "account/getSite",
    }),
    async selectPeriod(period) {
      if (period != this.selectedPeriod) {
        await this.$store.dispatch("pricing/setSpecial", null);
        this.setChosenPeriod(period);
        this.setChosenProduct(this.products[0].productid);
      }
      if (this.selectedPeriod === "MONTH")
        this.$store.dispatch("pricing/initialisePrices", false);
    },
    productButtonContent(productid) {
      return this.selectedProduct.productid === productid
        ? "Selected"
        : "Select";
    },
    datediff(date) {
      return date != null ? Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
    }
  },
  mounted () {
    if (this.subscription == null || this.subscription.specialid == null) { this.setSpecialAndPrices(null) }
    console.log(JSON.stringify(this.subscription))
    this.$store.dispatch("plan/initialiseSubscription");
    this.$store.dispatch("account/getAccount");
    this.setChosenPeriod("MONTH");
    this.getSite().then(() => {
      if (this.onnet) checkMyStatus();
    });
    if (this.subscription === null) {
      this.$store.dispatch("pricing/setSpecial", null);
    }
    this.$store.dispatch("pricing/initialisePrices", false);
  },
};
</script>

<style lang="scss" scoped>
.cut-btn {
  width: 110px;
  color: white;
  background-color: rgb(57, 18, 61, 0.6);

  border-radius: 20px 20px 20px 20px;
  cursor: pointer;

  font-weight: 500;
  margin: 3px 2px 3px 2px;

  &:hover {
    background-color: rgb(139, 0, 139, 1);
  }
}

.cut-btn:checked {
  box-shadow: none;
}

.selected {
  background-color: rgb(139, 0, 139, 1);
  box-shadow: none;
}

.central {
  margin-left: auto;
  margin-right: auto;
}

.flexi-input {
  margin-left: 1rem;
  margin-right: 1rem;
  max-width: 16rem;
}

.mastercard-icon {
  max-height: 2.2rem;
  padding-left: 0.5rem;
}

.visa-icon {
  max-height: 1rem;
}

.maestro-icon {
  max-height: 2rem;
  padding-left: 0.2rem;
}

.jcb-icon {
  max-height: 2rem;
  padding-left: 0.2rem;
}

.centre-content {
  margin-left: auto;
  margin-right: auto;
}
</style>